<template>
    <div v-if="$vuetify.breakpoint.smAndUp" class="d-flex align-center" style="position: fixed; left: 50%; bottom: 0px; z-index: 100; transform: translateX(-50%)">
        <v-snackbar
            :value="showDemoSnackbar"
            style="position: relative"
            :timeout="0"
            color="error"
            class="mr-2 demo-account-snackbar"
        >
            <div class="body-1" style="white-space: nowrap">{{ $t('Demo company account') }}</div>
            <v-tooltip top>
                <template v-slot:activator="{ on }">
                    <div class="ml-2" v-on="on">
                        <icon class="ml-2" icon="Info" color="white"/>
                    </div>
                </template>
                {{ $t('Demo account will be deleted later. Do this for testing purposes only!') }}
            </v-tooltip>
            <v-btn
                v-if="!isLoading"
                color="white"
                @click="onDemoAccountClick"
            >
                <span>{{ demoAccountButtonText }}</span>
            </v-btn>
        </v-snackbar>
        <v-snackbar
            :value="showSnackbar != null"
            :timeout="0"
            style="position: relative"
            bottom
            multi-line
            class="free-trial-snackbar"
        >
            <div class="body-1" style="white-space: nowrap">{{ message }}</div>
            <v-btn
                color="white"
                @click="onSubscribeClick"
            >
                <span>{{ $t("Subscribe") }}</span>
            </v-btn>
        </v-snackbar>
    </div>
</template>

<script>
import {computed, ref} from "@vue/composition-api";
import {$t} from "@/i18n";
import {calcDateDiff, parseSysDate} from "@/plugins/formatting";
import AccountModule from "@/store/modules/account";
import RightsModule from "@/composables/use-rights";
import AppModule from "@/store/modules/app";
import ApiDemo from '@/api/demo/demo'
import {uku_send_event, UKU_ACTIVITIES} from "@/helpers/uku_activity";

export default {
    name: "TrialEndSnackBar",
    setup(props, ctx) {
        const {accounts_info, current_account} = AccountModule();
        const {can_edit_account} = RightsModule();

        const isLoading = ref(false);

        const canStartDemo = computed(() => {
            const accountCreationDate = new Date(
                parseInt(current_account.value.created_ts)
            );
            const now = new Date(Date.now());
            if (current_account.value.data.show_demo === undefined && now < accountCreationDate.calcAddDays(7)) {
                return true
            }
            return false
        })

        const canEndDemo = computed(() => current_account.value.data.show_demo === 1)

        const showDemoSnackbar = computed(() => {
            if (ctx.root._route.meta && ctx.root._route.meta.wizard_mode) {
                return false
            }

            return canStartDemo.value || canEndDemo.value
        })

        const isDemoActive = computed(() => canStartDemo.value || canEndDemo.value)

        const demoAccountButtonText = computed(() => {
            if (canStartDemo.value) {
                return $t("Start demo")
            } else if (canEndDemo.value) {
                return $t("End demo")
            }
        })

        const onDemoAccountClick = async () => {
            if (canStartDemo.value) {
                try {
                    await ApiDemo.startDemo();
                    await uku_send_event({event: UKU_ACTIVITIES.TUTORIAL_BEGIN, info: "TrialEndSnackBar.onDemoAccountClick"})
                    location.assign('/dashboard');
                } catch (err) {
                    console.error("Demo start", err);
                }
            } else {
                isLoading.value = true
                try {
                    await ApiDemo.endDemo();
                    await uku_send_event({event: UKU_ACTIVITIES.TUTORIAL_COMPLETE, info: "SetupSpinner.load_data"})
                    location.assign('/dashboard');
                } catch (err) {
                    console.error("Demo end", err);
                }
            }
        }

        const showSnackbar = computed(() => {
            // mobile
            if (ctx.root.$vuetify.breakpoint.xsOnly) return null;
            // mini uku
            if (ctx.root._route.query.ukuwinversion !== undefined) return null;
            // wizard mode
            if (ctx.root._route.meta.wizard_mode !== undefined && ctx.root._route.meta.wizard_mode) return null;

            for (let i = 0; i < accounts_info.value.length; i++) {
                let company = accounts_info.value[i]
                if (company.type !== "company") continue;
                if (!can_edit_account(company.id)) continue;
                if (company.data && company.data.is_demo) continue;
                if (company.bundle_trial_until == null) continue;

                let today = new Date();
                let bundle_trial_until = parseSysDate(company.bundle_trial_until);
                if (company.is_demo && calcDateDiff.inDays(new Date(today.getFullYear(), today.getMonth(), today.getDate()), bundle_trial_until) <= 0) continue;

                return company;
            }
            return null;
        });
        const message = computed(() => {
            if (!showSnackbar.value) return "";
            let bundle_trial_until = parseSysDate(showSnackbar.value.bundle_trial_until);
            let today = new Date();
            let days_left = calcDateDiff.inDays(bundle_trial_until, new Date(today.getFullYear(), today.getMonth(), today.getDate()));
            return (days_left >= 0)
                ? $t("Free trial is expired - {0}").format(showSnackbar.value.name)
                : $t("Free trial {1} {2} left - {0}").format(showSnackbar.value.name, days_left * -1, days_left === -1 ? $t("day") : $t("days"));
        });

        const onSubscribeClick = () => {
            if (showSnackbar.value == null) return;
            ctx.root._router.push({name: "subscription_choose_plan", params: {id: showSnackbar.value.id}});
        };

        return {
            showSnackbar,
            message,
            onSubscribeClick,
            demoAccountButtonText,
            onDemoAccountClick,
            showDemoSnackbar,
            isLoading,
        };
    }
}
</script>
